import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useStudentsControllerGetDeepLinksTests } from "@oneapp/data";
import {
  getTestSeriesLabel,
  getVerticalIdFromProfilesData,
  ROUTESWEB,
} from "@repo/utils";
import { buildReportUrl } from "@repo/tests-web";
import { useAuthActions, useProfilesData } from "../../../store/useAuthStore";
import type { ParsedLink } from "../types";

const ILTS_BASE = `/${ROUTESWEB.APP.TESTS}/ilts`;

export function isILTSDeeplink(parsed: ParsedLink): boolean {
  return (
    parsed.module === "tests" &&
    (parsed.examType ?? "").toLowerCase() === "ilts" &&
    Boolean(parsed.scheduleId) &&
    Boolean(parsed.cohortAsmId) &&
    Boolean(parsed.testId)
  );
}

type Props = {
  scheduleId: string;
  cohortAsmId: string;
  examid: string;
};

export const ILTSDeeplinkHandler = ({
  scheduleId,
  cohortAsmId,
  examid,
}: Props) => {
  const navigate = useNavigate();
  const { setIsSyncing } = useAuthActions();
  const profilesData = useProfilesData();
  const testSeriesLabel = getTestSeriesLabel(
    getVerticalIdFromProfilesData(profilesData),
  );
  const testSeriesLabelRef = useRef(testSeriesLabel);
  useEffect(() => {
    testSeriesLabelRef.current = testSeriesLabel;
  });
  const { mutateAsync } = useStudentsControllerGetDeepLinksTests({
    request: { clientName: "oneApp" },
  });

  const navigateRef = useRef(navigate);
  const setIsSyncingRef = useRef(setIsSyncing);
  const mutateAsyncRef = useRef(mutateAsync);
  useEffect(() => {
    navigateRef.current = navigate;
    setIsSyncingRef.current = setIsSyncing;
    mutateAsyncRef.current = mutateAsync;
  });

  const hasCalled = useRef(false);

  useEffect(() => {
    if (!scheduleId || !cohortAsmId || !examid) return;
    if (hasCalled.current) return;
    hasCalled.current = true;

    setIsSyncingRef.current(true);

    const run = async () => {
      try {
        const response = (await mutateAsyncRef.current({
          data: {
            testType: "ilts",
            scheduleId,
            examId: examid,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            cohortAsmId,
          } as any,
        })) as any;

        const slot = response?.data?.test?.exam_slots?.[0];
        const assessment = slot?.assessment;
        const exam = assessment?.exams?.[0];

        const examId = exam?.exam_id ?? examid;
        const questionPaperId = exam?.question_paper_id ?? "";
        const examMode = exam?.exam_mode;
        const durationMinutes = exam?.duration_in_minutes;
        const examStatus = (exam?.status ?? "").toLowerCase();

        if (!exam || !examMode || !durationMinutes) {
          navigateRef.current(ILTS_BASE, { replace: true });
          return;
        }

        const title = exam?.title || "ILTS Test";
        const detailReportAvailableAfter = exam?.detail_report_available_after;
        const answerSheetUploadDurationMinutes =
          exam?.answer_sheet_upload_duration_minutes;
        const startTime = exam?.start_time;
        const studentStartTime = exam?.student_start_time;
        const studentEndTime = exam?.student_end_time;

        const baseExamState = {
          examId,
          questionPaperId,
          durationMinutes,
          title,
          instructions: exam?.instructions ?? "",
          startTime,
          examMode,
          answerSheetUploadDurationMinutes,
          detailReportAvailableAfter,
          currentPage: testSeriesLabelRef.current,
          pathToNav: ILTS_BASE,
        };

        if (examStatus === "in_progress" || examStatus === "started") {
          navigateRef.current(`/${ROUTESWEB.APP.TESTS}/exam`, {
            replace: true,
            state: {
              ...baseExamState,
              isResume: examStatus === "in_progress",
              examStartedAt: studentStartTime,
              examEndTime: studentEndTime,
            },
          });
          return;
        }

        if (examStatus === "attempted" || examStatus === "markscalculated") {
          const url = buildReportUrl({
            examId,
            assessmentId: assessment?.assessment_id ?? assessment?.id,
            questionPaperId,
            src: "ilts",
            studentEndTime,
            startTime: studentStartTime,
            detailReportAvailableAfter,
            fromViewReport: true,
            examMode,
            title,
          });
          navigateRef.current(url, { replace: true });
          return;
        }

        if (examStatus === "missed") {
          navigateRef.current(`/${ROUTESWEB.APP.TESTS}/solutions`, {
            replace: true,
            state: {
              examId,
              questionPaperId,
              title,
              currentPage: testSeriesLabelRef.current,
              pathToNav: `${ILTS_BASE}?tab=missed`,
            },
          });
          return;
        }

        navigateRef.current(`/${ROUTESWEB.APP.TESTS}/general-instructions`, {
          replace: true,
          state: baseExamState,
        });
      } catch (err) {
        console.error("[ILTSDeeplinkHandler] Failed to resolve deeplink:", err);
        navigateRef.current(ILTS_BASE, { replace: true });
      } finally {
        setIsSyncingRef.current(false);
      }
    };

    run();
  }, [scheduleId, cohortAsmId, examid]);

  return null;
};
